/*
 * <product-spinner>: a drag-to-spin 3D view of a product built from its front and
 * back mockup images. Rendered by snippets/product-spinner.liquid.
 *
 * Each view (one per color) is a flat card: the front mockup on one face and the
 * back mockup on the other, like spinning a piece of cardboard. Three.js is served
 * from the theme's own assets (Shopify CDN) and loaded on demand. If WebGL or the
 * script is unavailable the fallback <img> stays visible.
 *
 * Speed: textures are requested at the size the canvas actually needs, decoded off
 * the critical path, and every color is preloaded and uploaded to the GPU in the
 * background, so switching colors swaps instantly instead of waiting on the network.
 *
 * Design close-ups: a tap (not a drag) on the print area of the front face opens a
 * native <dialog> with the design image, and a tap on the print on the back opens one
 * with the back phrase as large live text; any tap closes it. The hit test is one
 * raycast against the two faces and only runs on tap or mouse hover.
 */
if (!customElements.get('product-spinner')) {
  const THREE_FALLBACK_URL = 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js';
  const TEXTURE_WIDTHS = [600, 800, 1000, 1200, 1400]; // Shopify CDN resizes to these on request
  const PRELOAD_DELAY = 800; // ms after the first color shows before preloading the others
  const PRELOAD_GAP = 60; // ms between background texture uploads, to keep the main thread free
  const TAP_DISTANCE = 8; // px of movement allowed for a tap
  const TAP_TIME = 500; // ms
  const DRAG_SPEED = 0.011;
  const TILT_SPEED = 0.004;
  const MAX_TILT = 0.32;
  const DAMPING = 0.93;
  const IDLE_SPIN = 0.4; // radians per second
  const IDLE_DELAY = 2500; // ms after the last touch before idle spin resumes

  customElements.define(
    'product-spinner',
    class ProductSpinner extends HTMLElement {
      constructor() {
        super();
        this.views = JSON.parse(this.querySelector('[data-views]').textContent);
        this.stage = this.querySelector('.product-spinner__stage');
        this.rotation = { x: 0, y: 0 };
        this.velocity = 0;
        this.dragging = false;
        this.visible = true;
        this.textures = new Map();
        this.lastInteraction = 0;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.designDialogs = [...this.querySelectorAll('[data-design-zoom]')];
        this.zones = []; // print areas you can tap, filled in once the faces exist
      }

      connectedCallback() {
        this.init().catch((error) => {
          console.warn('product-spinner: showing static image instead', error);
          this.classList.add('product-spinner--fallback');
        });

        // React to the swatch click itself instead of waiting for Dawn's section
        // re-render round trip (variantChange below stays as a fallback).
        this.productInfo = this.closest('product-info') || document;
        this.onOptionChange = (event) => {
          const value = event.target && event.target.value;
          if (value && Object.prototype.hasOwnProperty.call(this.views, value)) this.showView(value);
        };
        this.productInfo.addEventListener('change', this.onOptionChange);

        this.querySelectorAll('[data-design-zoom-open]').forEach((button) => {
          button.addEventListener('click', () => this.openDesign(this.designDialog(button.dataset.designZoomOpen)));
        });
        this.designDialogs.forEach((dialog) => {
          // any tap closes it; ignore the tail of the tap that opened it
          dialog.addEventListener('click', () => {
            if (performance.now() - this.designOpenedAt > 250) dialog.close();
          });
          dialog.addEventListener('close', () => {
            this.openDialog = null;
            this.lastFrame = performance.now();
          });
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event?.data?.variant;
            if (!variant) return;
            const position = Number(this.dataset.colorPosition);
            const key = position > 0 ? variant.options[position - 1] : 'default';
            this.showView(key);
          });
        }
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.productInfo?.removeEventListener('change', this.onOptionChange);
        clearTimeout(this.preloadTimer);
        cancelAnimationFrame(this.raf);
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        this.renderer?.dispose();
      }

      async init() {
        const THREE = await import(this.dataset.threeSrc || THREE_FALLBACK_URL);
        this.THREE = THREE;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setClearColor(0x000000, 0);
        this.renderer = renderer;

        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(28, 1, 0.1, 50);
        this.camera.position.set(0, 0, 2.1); // shirt fills a little more of the canvas

        this.scene.add(new THREE.AmbientLight(0xffffff, 2.3));
        const key = new THREE.DirectionalLight(0xffffff, 1.1);
        key.position.set(1.5, 2, 3);
        this.scene.add(key);
        const rim = new THREE.DirectionalLight(0xffffff, 0.9);
        rim.position.set(-1.5, 1.5, -3);
        this.scene.add(rim);

        this.group = new THREE.Group();
        this.scene.add(this.group);

        this.textureWidth = this.pickTextureWidth(renderer.getPixelRatio());

        const geometry = this.buildShell(THREE);
        this.frontMaterial = this.makeMaterial(THREE);
        this.backMaterial = this.makeMaterial(THREE);
        const front = new THREE.Mesh(geometry, this.frontMaterial);
        this.frontMesh = front;
        const back = new THREE.Mesh(geometry, this.backMaterial);
        back.rotation.y = Math.PI;
        this.group.add(front, back);

        const box = (value) => (value || '').split(',').map(Number);
        this.zones = [
          { mesh: front, box: box(this.dataset.printBox), dialog: this.designDialog('front') },
          { mesh: back, box: box(this.dataset.backPrintBox), dialog: this.designDialog('back') },
        ].filter((zone) => zone.dialog && zone.box.length === 4);

        await this.showView(this.dataset.initialView);

        this.stage.appendChild(renderer.domElement);
        this.resize();
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this.stage);
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
        });
        this.intersectionObserver.observe(this);

        this.bindPointer();
        this.classList.add('product-spinner--ready');
        this.lastFrame = performance.now();
        this.lastInteraction = this.lastFrame; // face front briefly before the idle spin starts
        this.loop = this.loop.bind(this);
        this.raf = requestAnimationFrame(this.loop);

        this.preloadTimer = setTimeout(() => this.preloadOtherViews(), PRELOAD_DELAY);
      }

      // The shirt fills most of the square canvas, so a texture about as wide as the
      // canvas in device pixels is enough. Smaller textures download, decode and
      // upload faster and use far less GPU memory on phones.
      pickTextureWidth(pixelRatio) {
        const needed = (this.stage.clientWidth || 600) * pixelRatio;
        return TEXTURE_WIDTHS.find((w) => w >= needed) || TEXTURE_WIDTHS[TEXTURE_WIDTHS.length - 1];
      }

      sizedUrl(url) {
        if (!url) return url;
        const width = this.textureWidth || 1000;
        return /[?&]width=\d+/.test(url)
          ? url.replace(/([?&]width=)\d+/, '$1' + width)
          : url + (url.includes('?') ? '&' : '?') + 'width=' + width;
      }

      // Load every other color in the background, one texture at a time, and push
      // each to the GPU so a later swatch click is just a material swap.
      async preloadOtherViews() {
        if (navigator.connection && navigator.connection.saveData) return;
        const names = Object.keys(this.views).filter((name) => name !== this.currentView);
        for (const name of names) {
          const view = this.views[name];
          for (const url of [view.front, view.back]) {
            if (!this.isConnected) return;
            try {
              const texture = await this.loadTexture(url);
              if (texture && this.renderer.initTexture) this.renderer.initTexture(texture);
            } catch (error) {
              // a failed preload just means that color loads on demand later
            }
            await new Promise((resolve) => setTimeout(resolve, PRELOAD_GAP));
          }
        }
        this.warmDesigns();
      }

      designDialog(name) {
        return this.designDialogs.find((dialog) => dialog.dataset.designZoom === name) || null;
      }

      // Load the close-ups in the background so they open instantly: decode the
      // front design image and fetch the back phrase's font.
      warmDesigns() {
        if (this.designsWarmed) return;
        this.designsWarmed = true;
        this.designDialogs.forEach((dialog) => {
          const image = dialog.querySelector('img');
          if (image) {
            image.loading = 'eager';
            if (image.decode) image.decode().catch(() => {});
          }
          const text = dialog.querySelector('.design-zoom__text');
          const font = text && getComputedStyle(text).font;
          if (font && document.fonts?.load) document.fonts.load(font, text.textContent.trim()).catch(() => {});
        });
      }

      openDesign(dialog) {
        if (!dialog || dialog.open) return;
        this.warmDesigns();
        this.designOpenedAt = performance.now();
        this.dragging = false;
        this.velocity = 0;
        this.openDialog = dialog;
        dialog.showModal();
        const text = dialog.querySelector('.design-zoom__text');
        if (text) {
          this.fitText(text);
          // measure again once the font is in, in case it was still loading
          document.fonts?.load(getComputedStyle(text).font, text.textContent.trim())
            .then(() => this.fitText(text))
            .catch(() => {});
        }
      }

      // Store the phrase's width in em; the CSS scales it to fill the screen width on one line.
      fitText(text) {
        const size = parseFloat(getComputedStyle(text).fontSize);
        const width = text.offsetWidth; // layout width, unaffected by the dialog's zoom-in animation
        if (size && width) text.style.setProperty('--text-em-width', (width / size).toFixed(3));
      }

      // The print zone under a screen point on whichever face is toward the camera.
      printAt(clientX, clientY) {
        if (!this.zones.length) return null;
        const THREE = this.THREE;
        const rect = this.renderer.domElement.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        this.pointerNdc = this.pointerNdc || new THREE.Vector2();
        this.raycaster = this.raycaster || new THREE.Raycaster();
        this.pointerNdc.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
        this.raycaster.setFromCamera(this.pointerNdc, this.camera);
        // FrontSide materials: only the face toward the camera can be hit
        const hit = this.raycaster.intersectObjects(
          this.zones.map((zone) => zone.mesh),
          false
        )[0];
        if (!hit || !hit.uv) return null;
        const zone = this.zones.find((candidate) => candidate.mesh === hit.object);
        const [left, bottom, right, top] = zone.box;
        return hit.uv.x >= left && hit.uv.x <= right && hit.uv.y >= bottom && hit.uv.y <= top ? zone : null;
      }

      // A flat unit square. The back mesh is the same plane turned around, so the
      // two faces sit back to back with nothing in between.
      buildShell(THREE) {
        return new THREE.PlaneGeometry(1, 1);
      }

      makeMaterial(THREE) {
        return new THREE.MeshLambertMaterial({
          transparent: true,
          alphaTest: 0.05,
          side: THREE.FrontSide,
        });
      }

      loadTexture(rawUrl) {
        if (!rawUrl) return Promise.resolve(null);
        const url = this.sizedUrl(rawUrl);
        if (this.textures.has(url)) return this.textures.get(url);
        const THREE = this.THREE;
        const promise = new Promise((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.decoding = 'async';
          image.onerror = reject;
          image.onload = () => {
            // decode() finishes image decoding off the main thread where supported
            const decoded = image.decode ? image.decode().catch(() => {}) : Promise.resolve();
            decoded.then(() => {
              const texture = new THREE.Texture(image);
              texture.colorSpace = THREE.SRGBColorSpace;
              texture.generateMipmaps = false; // shown near 1:1, mipmaps would only cost memory
              texture.minFilter = THREE.LinearFilter;
              texture.needsUpdate = true;
              resolve(texture);
            });
          };
          image.src = url;
        });
        promise.catch(() => this.textures.delete(url));
        this.textures.set(url, promise);
        return promise;
      }

      async showView(name) {
        const view = this.views[name] || this.views[Object.keys(this.views)[0]];
        if (!view || !this.THREE) return;
        if (this.currentView === name && this.frontMaterial.map) return;
        this.currentView = name;
        const [front, back] = await Promise.all([this.loadTexture(view.front), this.loadTexture(view.back)]);
        if (this.currentView !== name) return; // a newer swatch click won
        const hadMaps = Boolean(this.frontMaterial.map);
        this.frontMaterial.map = front;
        this.backMaterial.map = back || front;
        if (!hadMaps) {
          // only the first map assignment changes the shader program
          this.frontMaterial.needsUpdate = true;
          this.backMaterial.needsUpdate = true;
        }
      }

      resize() {
        const size = this.stage.clientWidth;
        if (!size || !this.renderer) return;
        this.renderer.setSize(size, size, false);
        this.camera.aspect = 1;
        this.camera.updateProjectionMatrix();
      }

      bindPointer() {
        const stage = this.stage;
        stage.addEventListener('pointerdown', (event) => {
          if (event.button !== undefined && event.button !== 0) return;
          this.dragging = true;
          this.velocity = 0;
          this.lastX = event.clientX;
          this.lastY = event.clientY;
          this.downX = event.clientX;
          this.downY = event.clientY;
          this.downAt = performance.now();
          this.classList.add('product-spinner--touched');
          stage.setPointerCapture?.(event.pointerId);
        });
        stage.addEventListener('pointermove', (event) => {
          if (!this.dragging) {
            if (event.pointerType === 'mouse' && this.zones.length) {
              stage.classList.toggle('is-over-print', Boolean(this.printAt(event.clientX, event.clientY)));
            }
            return;
          }
          const dx = event.clientX - this.lastX;
          const dy = event.clientY - this.lastY;
          this.lastX = event.clientX;
          this.lastY = event.clientY;
          const step = dx * DRAG_SPEED;
          this.rotation.y += step;
          this.velocity = this.velocity * 0.6 + step * 0.4;
          this.rotation.x = Math.max(-MAX_TILT, Math.min(MAX_TILT, this.rotation.x + dy * TILT_SPEED));
        });
        const release = () => {
          if (!this.dragging) return;
          this.dragging = false;
          this.lastInteraction = performance.now();
        };
        stage.addEventListener('pointerup', (event) => {
          const isTap =
            this.downAt &&
            performance.now() - this.downAt < TAP_TIME &&
            Math.hypot(event.clientX - this.downX, event.clientY - this.downY) < TAP_DISTANCE;
          this.downAt = 0;
          const zone = isTap && this.printAt(event.clientX, event.clientY);
          if (zone) this.openDesign(zone.dialog);
        });
        stage.addEventListener('pointerup', release);
        stage.addEventListener('pointercancel', release);
        stage.addEventListener('lostpointercapture', release);
      }

      loop(now) {
        this.raf = requestAnimationFrame(this.loop);
        const dt = Math.min((now - this.lastFrame) / 1000, 0.05);
        this.lastFrame = now;
        if (!this.visible || document.hidden || this.openDialog) return;

        if (!this.dragging) {
          this.rotation.y += this.velocity * dt * 60;
          this.velocity *= DAMPING;
          if (Math.abs(this.velocity) < 0.0004) this.velocity = 0;
          if (this.velocity === 0 && !this.reduceMotion && now - this.lastInteraction > IDLE_DELAY) {
            this.rotation.y += IDLE_SPIN * dt;
          }
          this.rotation.x *= 0.92;
        }

        this.group.rotation.set(this.rotation.x, this.rotation.y, 0);
        this.renderer.render(this.scene, this.camera);
      }
    }
  );
}
